import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ProjectSummary } from '@/types'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { getProjects } from '@/lib/content'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { FinalCta } from '@/components/kit/final-cta'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { ProjectCard } from '@/components/work/project-card'

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

const PREFERRED = ['Fashion', 'Food', 'Tech', 'Beauty', 'Fitness', 'Lifestyle']

const method = [
    { stamp: 'Week 1 · Forecast', title: 'Read the room', body: 'We study the brand, the category and the feed, then write down one hunch worth backing and the number that will prove it.' },
    { stamp: 'Weeks 2 to 4 · Make', title: 'Build a series', body: 'Not a pile of posts. A recurring format with a name, a day and a reason for people to come back next week.' },
    { stamp: 'Every Monday · Read', title: 'Keep what works', body: 'Replies, saves and sends get counted. Winners get a sequel, losers get an honest note and a quiet exit.' }
]

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

interface WorkProps {
    projects: Array<ProjectSummary>
}

const Work: FC<WorkProps> = ({ projects }): ReactNode => {
    const [filter, setFilter] = useState('All')
    const [leaving, setLeaving] = useState<Array<string>>([])
    const items = useRef(new Map<string, HTMLDivElement>())
    const before = useRef(new Map<string, DOMRect>())
    const pending = useRef(false)

    const industries = Array.from(new Set(projects.map(project => project.industry).filter(Boolean)))
        .sort((a, b) => {
            const ia = PREFERRED.indexOf(a)
            const ib = PREFERRED.indexOf(b)

            return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
        })

    const filters = ['All', ...industries]
    const matches = (project: ProjectSummary, value: string) => value === 'All' || project.industry === value
    const visible = projects.filter(project => matches(project, filter))
    const columns = [visible.filter((_, index) => index % 2 === 0), visible.filter((_, index) => index % 2 === 1)]

    const capture = () => {
        before.current = new Map()
        items.current.forEach((element, id) => before.current.set(id, element.getBoundingClientRect()))
    }

    const choose = (next: string) => {
        if (next === filter) return

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (reduced) {
            setFilter(next)

            return
        }

        const going = visible.filter(project => !matches(project, next)).map(project => project.id)

        const apply = () => {
            capture()
            pending.current = true
            setLeaving([])
            setFilter(next)
        }

        if (going.length) {
            setLeaving(going)
            window.setTimeout(apply, 280)
        }
        else {
            apply()
        }
    }

    useIsomorphicLayoutEffect(() => {
        if (!pending.current) return

        pending.current = false

        let order = 0

        items.current.forEach((element, id) => {
            const previous = before.current.get(id)
            const current = element.getBoundingClientRect()

            if (previous) {
                const dx = previous.left - current.left
                const dy = previous.top - current.top

                if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
                    element.animate(
                        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
                        { duration: 750, easing: EASE }
                    )
                }
            }
            else {
                element.animate(
                    [{ opacity: 0, transform: 'translateY(40px) scale(0.94)' }, { opacity: 1, transform: 'none' }],
                    { duration: 700, delay: 80 + order * 90, easing: EASE, fill: 'backwards' }
                )
                order++
            }
        })
    }, [filter])

    return (
        <Layout>
            <Seo
                title='Work'
                description='Case notes from Updraft: six fashion, food, tech, beauty, fitness and lifestyle brands that caught the lift on social, with the numbers to prove it.'
            />

            <section
                aria-labelledby='work-title'
                className='relative overflow-hidden pb-12 pt-36 md:pb-16 md:pt-44'
            >
                <div
                    aria-hidden='true'
                    className='bg-grid mask-radial absolute inset-0'
                />

                <div className='relative mx-auto max-w-[1400px] px-5 md:px-10'>
                    <SectionLabel>Work · case notes</SectionLabel>

                    <SplitText
                        as='h1'
                        id='work-title'
                        lines={['Forecasts that', 'came true']}
                        italic='true'
                        className='mt-6 text-[clamp(3.25rem,10.5vw,9.5rem)] font-bold leading-[0.86] tracking-[-0.045em]'
                    />

                    <div className='mt-12 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                        <Reveal
                            as='p'
                            delay={250}
                            className='max-w-md text-lg leading-relaxed text-stone'
                        >
                            Six brands, six very different feeds and one thing in common: each of them had a hunch worth backing. Filter by industry, then open a case for the full story.
                        </Reveal>

                        <Reveal delay={350}>
                            <div
                                role='group'
                                aria-label='Filter by industry'
                                className='flex flex-wrap gap-2'
                            >
                                {filters.map(item => {
                                    const selected = item === filter
                                    const count = item === 'All' ? projects.length : projects.filter(project => project.industry === item).length
                                    const source = item === 'All' ? null : projects.find(project => project.industry === item)

                                    return (
                                        <button
                                            key={item}
                                            type='button'
                                            aria-pressed={selected}
                                            onClick={() => choose(item)}
                                            className={cn(
                                                'inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-[background-color,border-color,color] duration-300',
                                                selected ? 'border-ink bg-ink text-cream' : 'border-ink/12 bg-paper/60 text-ink hover:border-ink/40'
                                            )}
                                        >
                                            {source
                                                ? <span data-mdlfy-cms={`projects:${source.id}:industry`}>{item}</span>
                                                : item}
                                            <span className={cn('font-mono text-[10px]', selected ? 'text-ember' : 'text-stone')}>
                                                {count}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section
                aria-label='Case studies'
                className='mx-auto max-w-[1400px] px-5 pb-10 md:px-10'
            >
                <p
                    className='sr-only'
                    aria-live='polite'
                >
                    {visible.length === 1 ? 'Showing 1 case study' : `Showing ${visible.length} case studies`}
                </p>

                {projects.length ? (
                    <div className='grid gap-x-8 gap-y-14 md:grid-cols-2 md:gap-x-10'>
                        {columns.map((column, columnIndex) => (
                            <div
                                key={columnIndex}
                                className={cn('flex flex-col gap-14 md:gap-20', columnIndex === 1 && 'md:pt-32')}
                            >
                                {column.map((project, index) => (
                                    <div
                                        key={project.id}
                                        ref={element => {
                                            if (element) items.current.set(project.id, element)
                                            else items.current.delete(project.id)
                                        }}
                                        className={cn(
                                            'transition-[opacity,scale] duration-300 ease-out',
                                            leaving.includes(project.id) && 'scale-95 opacity-0'
                                        )}
                                    >
                                        <Reveal delay={index * 80}>
                                            <ProjectCard
                                                project={project}
                                                tall={(columnIndex + index) % 2 === 0}
                                            />
                                        </Reveal>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className='rounded-[2rem] border border-dashed border-ink/20 p-12 text-center'>
                        <p className='font-heading text-3xl font-bold tracking-[-0.04em]'>The case notes are being typed up.</p>
                        <p className='mt-3 text-stone'>Give us a moment and refresh. The stories are better than the wait.</p>
                    </div>
                )}
            </section>

            {projects.length > 0 && (
                <section
                    aria-labelledby='results-wall-title'
                    className='ticker mt-24 overflow-hidden border-y border-ink/15 bg-ink py-10 text-cream md:mt-32'
                >
                    <h2
                        id='results-wall-title'
                        className='mx-auto max-w-[1400px] px-5 font-mono text-[11px] uppercase tracking-[0.16em] text-ember md:px-10'
                    >
                        The results wall · every headline, no small print
                    </h2>
                    <ul
                        className='ticker-track mt-6 flex w-max items-center'
                        style={{ '--ticker-duration': '50s' } as CSSProperties}
                    >
                        {[...projects, ...projects].map((project, index) => (
                            <li
                                key={`${project.id}-${index}`}
                                aria-hidden={index >= projects.length}
                                className='flex shrink-0 items-baseline gap-4 border-r border-cream/15 px-8'
                            >
                                <span
                                    className='font-heading text-[clamp(2rem,4vw,3.5rem)] font-bold leading-none tracking-[-0.04em]'
                                    data-mdlfy-cms={project.headline_result ? `projects:${project.id}:headline_result` : undefined}
                                >
                                    {project.headline_result ?? project.brand}
                                </span>
                                <span
                                    className='font-mono text-xs uppercase tracking-[0.14em] text-cream/60'
                                    data-mdlfy-cms={`projects:${project.id}:brand`}
                                >
                                    {project.brand}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section
                aria-labelledby='method-title'
                className='mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28'
            >
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <SectionLabel>The method</SectionLabel>
                        <SplitText
                            as='h2'
                            id='method-title'
                            lines={['Every case starts', 'the same way']}
                            italic='same'
                            className='mt-5 text-[clamp(2.5rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                        />
                    </div>
                    <Reveal
                        as='p'
                        delay={150}
                        className='text-lg leading-relaxed text-stone lg:col-span-5'
                    >
                        Different brands, different apps, the same three habits. None of them are secret, which is sort of the point.
                    </Reveal>
                </div>

                <ol className='mt-12 grid gap-5 md:grid-cols-3'>
                    {method.map((step, index) => (
                        <Reveal
                            as='li'
                            key={step.title}
                            delay={index * 100}
                            settle={[-1.5, 1, -0.8][index]}
                        >
                            <div className='relative h-full rounded-[1.75rem] border border-ink/10 bg-paper p-7'>
                                <span
                                    aria-hidden='true'
                                    className='tape -top-3 left-8 -rotate-3'
                                />
                                <p className='font-mono text-xs uppercase tracking-[0.16em] text-ember'>{step.stamp}</p>
                                <h3 className='mt-4 text-3xl font-bold tracking-[-0.04em]'>{step.title}</h3>
                                <p className='mt-3 text-base leading-relaxed text-stone'>{step.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </ol>
            </section>

            <FinalCta
                lines={['Your brand,', 'next on this page']}
                italic='next'
                body='Tell us where your feed is today and where you want it to be. You will get a free forecast with the first three ideas we would make.'
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<WorkProps> = async () => {
    try {
        const projects = await getProjects()

        return { props: { projects }, revalidate: 60 }
    }
    catch (error) {
        console.error('Work page content could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { projects: [] }, revalidate: 30 }
    }
}

export default Work
